import { BackspaceIcon } from '@/components/icons';
import { isSbConnectUser } from '@/helpers/helper';
import { useAuth } from '@/hooks';
import { alpha, brand, text } from '@/styles/shared';
import { Check, Close } from '@mui/icons-material';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
  clearTextButtonStyles,
  contentAreaStyles,
  contentStyles,
  deleteButtonStyles,
  headerStyles,
  keypadButtonStyles,
  modalStyles,
  patientIdDisplayStyles,
  submitButtonStyles,
} from './styles';

interface PatientIdModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (patientId: string) => void;
  onSkip?: () => void;
  initialValue?: string;
}

export const PatientIdModal: React.FC<PatientIdModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onSkip,
  initialValue,
}) => {
  const [patientId, setPatientId] = useState(initialValue ?? '');
  const [error, setError] = useState('');
  const { token } = useAuth();
  const isSbConnect = isSbConnectUser(token);

  const handleKeyPress = (key: string) => {
    // Enforce: 'E' can only be the first character
    if (key === 'E' && patientId.length > 0) return;

    if (patientId.length < 9) {
      const newId = patientId + key;
      setPatientId(newId);

      // Clear error when user starts typing
      if (error) setError('');
    }
  };

  const handleDelete = () => {
    setPatientId(patientId.slice(0, -1));
    if (error) setError('');
  };

  const handleSubmit = () => {
    if (patientId.length < 6 || patientId.length > 9) {
      setError('Patient ID must be between 6-9 characters');
      return;
    }
    onSubmit(patientId);
    setPatientId('');
    setError('');
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      onClose();
    }
    setPatientId('');
    setError('');
  };

  const handleClose = () => {
    onClose();
    setPatientId('');
    setError('');
  };

  // Close when clicking the dark area (backdrop)
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.currentTarget === e.target) {
      handleClose();
    }
  };

  const keypadLayout = [
    ['E'],
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['0'],
  ];

  // Prepopulate when opening
  React.useEffect(() => {
    if (isOpen) {
      setPatientId(initialValue ?? '');
      setError('');
    }
  }, [isOpen, initialValue]);

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      disableAutoFocus
      disableEnforceFocus
    >
      <Box sx={modalStyles} onClick={handleBackdropClick}>
        <Box sx={contentStyles} onClick={e => e.stopPropagation()}>
          {/* Header */}
          <Box sx={headerStyles}>
            <Typography
              variant="h6"
              sx={{
                ...text.title24,
                fontSize: { xs: '16px', lg: '18px' },
                fontWeight: 600,
                color: '#000000',
              }}
            >
              Enter Patient ID
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                padding: { xs: '6px', lg: '8px' },
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: alpha.purple05,
                  borderColor: brand.purpleAccent,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                  transition: 'all 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
                },
              }}
            >
              <Close
                sx={{
                  fontSize: { xs: '18px', lg: '20px' },
                  color: '#666666',
                }}
              />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={contentAreaStyles}>
            {/* Patient ID Display */}
            <Box sx={patientIdDisplayStyles}>
              <Typography
                sx={{
                  fontSize: { xs: '22px', lg: '28px' },
                  fontFamily: 'monospace',
                  color: '#333333',
                  letterSpacing: '0.2em',
                  fontWeight: 500,
                }}
              >
                {patientId || ''}
              </Typography>
              {patientId.length > 0 && (
                <Button
                  aria-label="Clear all"
                  onClick={() => {
                    setPatientId('');
                    setError('');
                  }}
                  sx={clearTextButtonStyles}
                >
                  Clear
                </Button>
              )}
            </Box>

            {/* Error/Help Text */}
            {error ? (
              <Typography
                sx={{
                  color: brand.greenPrimary,
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '24px',
                }}
              >
                {error}
              </Typography>
            ) : (
              <Typography
                sx={{
                  color: '#999999',
                  textAlign: 'center',
                  fontSize: { xs: '11px', lg: '12px' },
                  marginBottom: { xs: '16px', lg: '24px' },
                }}
              >
                Patient ID must be between 6-9 characters
              </Typography>
            )}

            {/* Keypad */}
            <Box sx={{ marginBottom: { xs: '16px', lg: '24px' } }}>
              {keypadLayout.map((row, rowIndex) => (
                <Box
                  key={rowIndex}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: { xs: '8px', lg: '12px' },
                    marginBottom: { xs: '8px', lg: '12px' },
                  }}
                >
                  {row.map(key => (
                    <Button
                      key={key}
                      onClick={() => handleKeyPress(key)}
                      disabled={
                        patientId.length >= 9 ||
                        (key === 'E' && patientId.length > 0)
                      }
                      sx={keypadButtonStyles}
                    >
                      {key}
                    </Button>
                  ))}
                </Box>
              ))}
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: '8px', lg: '12px' },
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: { xs: '12px', lg: '16px' },
              }}
            >
              <Button
                onClick={handleDelete}
                disabled={patientId.length === 0}
                sx={deleteButtonStyles}
              >
                <BackspaceIcon stroke="#666666" />
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={patientId.length < 6 || patientId.length > 9}
                sx={submitButtonStyles}
              >
                <Check sx={{ fontSize: { xs: '18px', lg: '20px' } }} />
              </Button>
            </Box>

            {/* Skip Button - only for SB-Lite (non-sb-connect) users */}
            {onSkip && !isSbConnect && (
              <Button
                onClick={handleSkip}
                sx={{
                  width: '100%',
                  padding: '12px',
                  color: '#666666',
                  fontSize: '15px',
                  textTransform: 'none',
                  borderRadius: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: alpha.purple05,
                    color: brand.purpleAccent,
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                    transition: 'all 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
                  },
                }}
              >
                Skip for now
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
